/**
 * Train panel — DOM list of trainable units for the selected barracks.
 * Shows cost, build time, and the live queue; hidden when no training
 * building is selected.
 */
import type { UnitDef } from "../data/types";
import type { PlacedBuilding } from "../sim/buildings";
import type { Economy } from "../sim/economy";
import { unitCost, type TrainingQueues } from "../sim/training";
import { consumesCommand, type CommandStatus } from "../sim/command";
import { tierById } from "../sim/organization";
import {
  MEDALLION_PX,
  type MedallionRenderer,
  type MedallionSpec,
} from "../render/medallions";
import type { ResolvedModel } from "../data/artmap";

/** Where the train panel gets medallions (wired by main.ts). */export interface TrainMedallions {
  renderer: MedallionRenderer;
  /** Faction tint for the portraits. */
  tint: number;
  /** artKeyFor(db.unitModel(unit.id), unit.id). */
  artKeyFor(unit: UnitDef): string;
  /** db.unitModel(unit.id) — feeds the medallion MODEL-SWAP hook. */
  modelFor(unit: UnitDef): ResolvedModel;
}

/** 44px touch targets on coarse-pointer (phone) devices. */
const TOUCH_PAD =
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches
    ? "12px 10px"
    : "6px 8px";

/** Organization-muster info for a trainable military unit (tier-scaled). */
export interface MusterInfo {
  tierId: string;
  tierName: string;
  paperStrength: number;
  cost: Record<string, number>;
  buildTimeSec: number;
  upkeepFoodPerMin: number;
}

function costLabel(cost: Record<string, number>): string {
  const parts = Object.entries(cost).map(([k, v]) => `${v} ${k}`);
  return parts.length > 0 ? parts.join(", ") : "free";
}

export class TrainPanel {
  onTrain: ((unit: UnitDef, tierId: string) => void) | null = null;
  /** Set by main.ts; when null, buttons render without portraits. */
  medallions: TrainMedallions | null = null;
  private el: HTMLDivElement;

  constructor() {
    this.el = document.createElement("div");
    this.el.id = "train-panel";
    this.el.style.cssText =
      "position:fixed;right:12px;top:64px;width:240px;max-height:70vh;overflow:auto;" +
      "background:rgba(10,10,12,0.82);border:1px solid #3a2f22;border-radius:8px;" +
      "padding:8px;font-size:13px;display:none;";
    document.body.appendChild(this.el);
  }

  /** Portrait canvas for a train button (empty when medallions unwired). */
  private medallionHtml(u: UnitDef): string {
    if (!this.medallions) return "";
    const name = u.name_en.replace(/"/g, "&quot;");
    const artKey = this.medallions.artKeyFor(u).replace(/"/g, "&quot;");
    return (
      `<canvas class="train-medallion" width="${MEDALLION_PX}" height="${MEDALLION_PX}" ` +
      `data-artkey="${artKey}" data-name="${name}" data-unit="${u.id}" ` +
      `style="float:left;width:44px;height:44px;margin:2px 8px 2px 0;border-radius:50%;` +
      `background:radial-gradient(circle at 50% 38%, #2a2d33, #141518);` +
      `border:1px solid #b98b3a;"></canvas>`
    );
  }

  render(
    building: PlacedBuilding | null,
    units: UnitDef[],
    economy: Economy,
    queues: TrainingQueues,
    command?: CommandStatus,
    musters?: (unit: UnitDef) => MusterInfo[],
  ): void {
    if (!building || building.state !== "active") {
      this.el.style.display = "none";
      return;
    }
    this.el.style.display = "block";
    const commandLabel = command
      ? ` <span style="color:#b98b3a;" title="Military command: 1 formation = 1 command. Build military buildings or research Dilectus to raise the cap.">⌘ ${command.used}/${command.total}</span>`
      : "";
    const q = queues.queueOf(building.uid);
    const queueHtml = q.length > 0
      ? `<div style="color:#9a917f;margin:6px 0 2px;">Queue (${q.length})</div>` +
        q.map((o, i) => {
          const pct = i === 0 ? queues.headProgress(building.uid) : 0;
          const tierName = o.tierId
            ? `<span style="color:#b98b3a;">${tierById(o.tierId)?.name_en ?? o.tierId}</span> · `
            : "";
          return `<div style="margin:3px 0;color:#e8e2d4;">${tierName}${o.unitId}` +
            (i === 0
              ? `<div style="height:4px;background:#2a2d33;border-radius:2px;margin-top:2px;">` +
                `<div style="height:100%;width:${Math.round(pct * 100)}%;background:#b98b3a;border-radius:2px;"></div></div>`
              : "") +
            `</div>`;
        }).join("")
      : "";
    const unitsHtml = units
      .map((u) => {
        const options = musters?.(u) ?? [];
        // Workers (and any unit with no muster options) train as ordinary
        // single units: one button, unscaled cost/time.
        const variants: MusterInfo[] = options.length > 0
          ? options
          : [{
            tierId: "", tierName: u.name_en, paperStrength: 1,
            cost: unitCost(u),
            buildTimeSec: (u.extra["build_time"] as number) ?? 20,
            upkeepFoodPerMin: 0,
          }];
        return variants
          .map((mi) => {
            const cost = mi.cost;
            // Command gate: command-consuming units are blocked at cap;
            // workers never are.
            const capped =
              !!command &&
              consumesCommand(u) &&
              command.used >= command.total;
            const afford = economy.canAfford(cost) && !capped;
            const isMuster = mi.tierId !== "";
            const tierLabel = isMuster
              ? `<span style="color:#b98b3a;">${mi.tierName}</span> · ${mi.paperStrength} paper · upkeep ${mi.upkeepFoodPerMin.toFixed(1)} food/min<br/>`
              : "";
            const tip = capped
              ? `Command capacity reached (${command.used}/${command.total}) — build military buildings or research Dilectus`
              : afford
                ? (u.extra["description"] ?? u.name_en)
                : "Insufficient resources";
            return (
              `<button data-unit="${u.id}" data-tier="${mi.tierId}" ${afford ? "" : "disabled"} ` +
              `title="${tip}" ` +
              `style="display:block;width:100%;text-align:left;margin:4px 0;padding:${TOUCH_PAD};` +
              `background:#1a1c20;color:#e8e2d4;border:1px solid #3a2f22;border-radius:5px;` +
              `cursor:${afford ? "pointer" : "not-allowed"};opacity:${afford ? 1 : 0.45};">` +
              `${this.medallionHtml(u)}` +
              `<b>${u.name_en}</b>${isMuster ? ` <span style="color:#9a917f;">— muster as</span>` : ""}<br/>` +
              `${tierLabel}` +
              `<span style="color:#9a917f;font-size:12px;">${costLabel(cost)} · ${Math.round(mi.buildTimeSec)}s` +
              `</span><span style="clear:both;display:block;"></span></button>`
            );
          })
          .join("");
      })
      .join("");
    this.el.innerHTML =
      `<div style="color:#9a917f;margin-bottom:4px;">TRAIN — ${building.def.name_en}${commandLabel}</div>` +
      unitsHtml + queueHtml;
    this.el.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        const unit = units.find((u) => u.id === btn.getAttribute("data-unit"));
        if (unit) this.onTrain?.(unit, btn.getAttribute("data-tier") ?? "");
      });
    });
    // Paint the medallion portraits (static cache — no per-frame work).
    if (this.medallions) {
      const src = this.medallions;
      this.el
        .querySelectorAll<HTMLCanvasElement>("canvas.train-medallion")
        .forEach((c) => {
          const unitId = c.getAttribute("data-unit") ?? "";
          const unit = units.find((u) => u.id === unitId);
          const spec: MedallionSpec = {
            artKey:
              c.getAttribute("data-artkey") ??
              `placeholder:${c.getAttribute("data-name") ?? "?"}`,
            name: c.getAttribute("data-name") ?? "?",
            tint: src.tint,
            model: unit ? src.modelFor(unit) : undefined,
          };
          src.renderer.attachStatic(c, spec);
        });
    }
  }
}
